import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { initEcho } from '../../services/notificationService';
import api from '../../services/api';
import { useAuth } from '../../App';
import { toast } from 'react-hot-toast';
import {
  BookOpen, Layers, Clock, AlertCircle, CheckCircle, RefreshCw,
  Trash2, ExternalLink, ShieldAlert, Plus, HelpCircle, Bell, Users, Calendar
} from 'lucide-react';

const BOOKS = [
  { id: 'propiedad', name: 'Registro de Propiedad', desc: 'Inscripciones de dominio y transferencias', color: 'var(--primary)', bg: 'rgba(37, 99, 235, 0.05)' },
  { id: 'hipotecas', name: 'Hipotecas y Gravámenes', desc: 'Garantías, hipotecas y servidumbres', color: '#b45309', bg: 'rgba(180, 83, 9, 0.05)' },
  { id: 'prohibiciones', name: 'Prohibiciones e Interdicciones', desc: 'Embargos, litigios y prohibiciones de enajenar', color: '#dc2626', bg: 'rgba(220, 38, 38, 0.05)' },
  { id: 'comercio', name: 'Registro de Comercio', desc: 'Sociedades, poderes y comercio', color: '#0d9488', bg: 'rgba(13, 148, 136, 0.05)' },
  { id: 'aguas', name: 'Registro de Aguas', desc: 'Derechos de aprovechamiento de aguas', color: '#2563eb', bg: 'rgba(37, 99, 235, 0.05)' },
  { id: 'hipotecas_aguas', name: 'Hipotecas de Aguas', desc: 'Hipotecas sobre derechos de aguas', color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.05)' },
  { id: 'prohibiciones_aguas', name: 'Prohibiciones de Aguas', desc: 'Prohibiciones sobre derechos de aguas', color: '#9333ea', bg: 'rgba(147, 51, 234, 0.05)' },
  { id: 'minas', name: 'Registro de Minas', desc: 'Pertenencias y concesiones mineras', color: '#4b5563', bg: 'rgba(75, 85, 99, 0.05)' }
];

export default function TomadorFojas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const echoRef = useRef(null);

  // Estados del tomador
  const [tipoRegistro, setTipoRegistro] = useState('propiedad');
  const [cantidadFojas, setCantidadFojas] = useState(1);
  const [siguienteCorrelativo, setSiguienteCorrelativo] = useState(null);
  const [loadingCorrelativo, setLoadingCorrelativo] = useState(false);
  const [correlativosLibros, setCorrelativosLibros] = useState({});

  // Estados de la tabla de pendientes
  const [pendientes, setPendientes] = useState([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);

  // Filtros de la grilla: quién tomó la foja y en qué rango de fechas
  const [filtroModo, setFiltroModo] = useState('todas'); // 'mias' | 'funcionario' | 'todas'
  const [filtroFuncionarioId, setFiltroFuncionarioId] = useState('');
  const [funcionarios, setFuncionarios] = useState([]);
  const hoyISO = new Date().toISOString().slice(0, 10);
  const [fechaDesde, setFechaDesde] = useState(hoyISO);
  const [fechaHasta, setFechaHasta] = useState(hoyISO);
  const [todasFechas, setTodasFechas] = useState(false);

  // Estados del banner de fojas liberadas (alerta/trencito)
  const [fojasLiberadas, setFojasLiberadas] = useState([]);
  const [mostrarLiberadas, setMostrarLiberadas] = useState(false);

  // Estados del modal de liberación
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [liberarId, setLiberarId] = useState(null);
  const [motivoLiberacion, setMotivoLiberacion] = useState('');
  const [submittingLiberar, setSubmittingLiberar] = useState(false);

  const userName = user
    ? [user.nombres, user.apellido_paterno].filter(Boolean).join(' ') || user.email || 'Funcionario'
    : 'Funcionario';

  // Cargar el correlativo del libro seleccionado
  const fetchUltimoCorrelativo = async (tipo) => {
    setLoadingCorrelativo(true);
    try {
      const { data } = await api.get(`/fojas/ultimo-correlativo`, {
        params: { tipo_registro: tipo }
      });
      if (data && data.status === 'success') {
        setSiguienteCorrelativo(data.data);
        // Actualizar el cache de correlativos por libro
        setCorrelativosLibros(prev => ({
          ...prev,
          [tipo]: data.data
        }));
      }
    } catch (error) {
      console.error('Error al obtener el correlativo:', error);
      toast.error('No se pudo obtener el correlativo actualizado.');
    } finally {
      setLoadingCorrelativo(false);
    }
  };

  // Cargar los correlativos para todos los libros (para las tarjetas visuales)
  const fetchCorrelativosTodos = async () => {
    try {
      const promesas = BOOKS.map(async (book) => {
        try {
          const { data } = await api.get(`/fojas/ultimo-correlativo`, {
            params: { tipo_registro: book.id }
          });
          return { id: book.id, data: data.data };
        } catch {
          return { id: book.id, data: null };
        }
      });
      const resultados = await Promise.all(promesas);
      const nuevoCache = {};
      resultados.forEach(res => {
        if (res.data) {
          nuevoCache[res.id] = res.data;
        }
      });
      setCorrelativosLibros(nuevoCache);
    } catch (e) {
      console.error('Error cargando correlativos de libros', e);
    }
  };

  // Cargar lista de fojas pendientes, respetando los filtros de usuario/fecha activos
  const fetchPendientes = async (overrides = {}) => {
    setLoadingPendientes(true);
    try {
      const modo = overrides.filtroModo ?? filtroModo;
      const usaTodasFechas = overrides.todasFechas ?? todasFechas;
      const params = {};

      if (modo === 'mias') {
        params.mias = true;
      } else if (modo === 'funcionario' && (overrides.filtroFuncionarioId ?? filtroFuncionarioId)) {
        params.usuario_responsable_id = overrides.filtroFuncionarioId ?? filtroFuncionarioId;
      }

      if (usaTodasFechas) {
        params.todas_fechas = 1;
      } else {
        params.fecha_desde = overrides.fechaDesde ?? fechaDesde;
        params.fecha_hasta = overrides.fechaHasta ?? fechaHasta;
      }

      const { data } = await api.get(`/fojas/pendientes`, { params });
      if (data && data.status === 'success') {
        setPendientes(data.data);
      }
    } catch (error) {
      console.error('Error al cargar fojas pendientes:', error);
      toast.error('Error al cargar la lista de fojas pendientes.');
    } finally {
      setLoadingPendientes(false);
    }
  };

  // Poblar el selector "Por funcionario" (reutiliza el endpoint ya existente de asignación de trámites)
  const fetchFuncionarios = async () => {
    try {
      const { data } = await api.get('/users/assignable');
      setFuncionarios(data?.data || []);
    } catch (error) {
      console.error('Error al cargar funcionarios:', error);
    }
  };

  // Cargar el contador/lista de fojas liberadas pendientes (alerta de "trencito")
  const fetchFojasLiberadas = async () => {
    try {
      const { data } = await api.get('/fojas/alertas');
      if (data && data.status === 'success') {
        setFojasLiberadas(data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar fojas liberadas:', error);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    fetchUltimoCorrelativo(tipoRegistro);
    fetchCorrelativosTodos();
    fetchPendientes();
  }, [tipoRegistro]);

  useEffect(() => {
    fetchFuncionarios();
    fetchFojasLiberadas();
  }, []);

  // Recargar la grilla cuando cambian los filtros de usuario/fecha
  useEffect(() => {
    fetchPendientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroModo, filtroFuncionarioId, fechaDesde, fechaHasta, todasFechas]);

  // Configuración de WebSockets en tiempo real
  useEffect(() => {
    if (!user) return;

    const token = JSON.parse(localStorage.getItem('cbr_auth') || '{}').token;
    if (!token) return;

    const echo = initEcho(token);
    if (!echo) return;

    echoRef.current = echo;

    echo.join('presence-chat')
      .listenForWhisper('foja_tomada', (data) => {
        // Mostrar un toast personalizado súper premium
        toast.custom((t) => (
          <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'}`}
            style={{
              maxWidth: '420px',
              width: '100%',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderLeft: `5px solid ${BOOKS.find(b => b.id === data.tipo_registro)?.color || 'var(--primary)'}`,
              borderTop: '1px solid rgba(0,0,0,0.05)',
              borderRight: '1px solid rgba(0,0,0,0.05)',
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              borderRadius: '10px',
              padding: '14px 16px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              pointerEvents: 'auto'
            }}
          >
            <div style={{ color: 'var(--copper)', marginTop: '2px' }}>
              <AlertCircle size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--primary-dark)' }}>
                Foja Reservada en Tiempo Real
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '4px', lineHeight: '1.4' }}>
                El funcionario <strong>{data.usuario}</strong> acaba de tomar la <strong>Foja {data.fojaInicio}{data.fojaTermino && data.fojaTermino !== data.fojaInicio ? `–${data.fojaTermino}` : ''}</strong>, <strong>Número {data.numero}</strong> en el <strong>{BOOKS.find(b => b.id === data.tipo_registro)?.name}</strong>.
              </div>
            </div>
          </div>
        ), { duration: 6000 });

        // Actualizar correlativo actual si es el mismo tipo o recargar general
        fetchCorrelativosTodos();
        if (tipoRegistro === data.tipo_registro) {
          fetchUltimoCorrelativo(data.tipo_registro);
        }
        fetchPendientes();
        fetchFojasLiberadas();
      });

    return () => {
      // Nota: No llamamos a echo.leave('presence-chat') aquí incondicionalmente
      // porque ChatWidget también usa este mismo canal global.
      // Laravel Echo maneja múltiples listeners en el mismo canal.
    };
  }, [user, tipoRegistro]);

  // Reservar/Tomar Foja
  const handleReservar = async (e, overrideCantidad = null) => {
    if (e && e.preventDefault) e.preventDefault();
    const cantidadAReservar = overrideCantidad ?? cantidadFojas;
    if (cantidadAReservar < 1) {
      toast.error('La cantidad de fojas debe ser al menos 1.');
      return;
    }

    const toastId = toast.loading('Impactando control correlativo y reservando fojas...');
    try {
      const { data } = await api.post('/fojas/reservar', {
        tipo_registro: tipoRegistro,
        cantidad_fojas: parseInt(cantidadAReservar, 10)
      });

      if (data && data.status === 'success') {
        const ids = data.data.registro_indices_ids;
        toast.success(data.message || 'Foja reservada correctamente.', { id: toastId });

        // Obtener el correlativo tomado para reportarlo por sockets
        // Llamamos al correlativo recién reservado. Si nos da el primer ID creado, podemos buscar sus datos.
        const primerId = ids[0];
        const fojaTermino = siguienteCorrelativo ? siguienteCorrelativo.foja_inicio + cantidadAReservar - 1 : null;

        // Notificar por websockets a los demás clientes
        if (echoRef.current && siguienteCorrelativo) {
          echoRef.current.join('presence-chat').whisper('foja_tomada', {
            usuario: userName,
            fojaInicio: siguienteCorrelativo.foja_inicio,
            fojaTermino,
            numero: siguienteCorrelativo.numero,
            tipo_registro: tipoRegistro
          });
        }

        // Recargar correlativos y tabla de pendientes
        setCantidadFojas(1);
        fetchUltimoCorrelativo(tipoRegistro);
        fetchCorrelativosTodos();
        fetchPendientes();
        fetchFojasLiberadas();

        // Opcional: preguntar si desea ir directamente a completar
        if (cantidadAReservar === 1) {
          toast((t) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.82rem' }}>¿Desea completar la información de la foja ahora?</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn-sm" style={{ background: 'var(--primary)', color: 'white', border: 'none', padding: '3px 10px', fontSize: '0.75rem' }}
                  onClick={() => {
                    toast.dismiss(t.id);
                    navigate(`/ingreso-indice?registro_indice_id=${primerId}`);
                  }}>
                  Sí, completar
                </button>
                <button className="btn btn-sm btn-secondary" style={{ padding: '3px 10px', fontSize: '0.75rem' }}
                  onClick={() => toast.dismiss(t.id)}>
                  Más tarde
                </button>
              </div>
            </div>
          ), { duration: 8000 });
        }
      }
    } catch (error) {
      console.error('Error al reservar fojas:', error);
      toast.error(error.response?.data?.message || 'Error al intentar reservar las fojas.', { id: toastId });
    }
  };

  // Abrir modal de liberación
  const openLiberarModal = (id) => {
    setLiberarId(id);
    setMotivoLiberacion('');
    setIsModalOpen(true);
  };

  // Confirmar liberación (trencito)
  const confirmLiberar = async () => {
    if (!motivoLiberacion.trim()) {
      toast.error('Debe ingresar un motivo para liberar la foja.');
      return;
    }

    setSubmittingLiberar(true);
    const toastId = toast.loading('Liberando foja...');
    try {
      const { data } = await api.post('/fojas/liberar', {
        registro_indice_id: liberarId,
        motivo: motivoLiberacion
      });

      if (data && data.status === 'success') {
        toast.success(data.message || 'Foja liberada al pool de reciclaje (trencito).', { id: toastId });
        setIsModalOpen(false);
        fetchUltimoCorrelativo(tipoRegistro);
        fetchCorrelativosTodos();
        fetchPendientes();
        fetchFojasLiberadas();
      }
    } catch (error) {
      console.error('Error al liberar foja:', error);
      toast.error(error.response?.data?.message || 'Error al liberar la foja.', { id: toastId });
    } finally {
      setSubmittingLiberar(false);
    }
  };

  // Tomar una foja liberada específica desde el banner de alertas.
  // El backend reutiliza automáticamente la liberada más antigua cuyo rango
  // (foja_termino - foja_inicio + 1) calce exacto con la cantidad pedida.
  const handleTomarLiberada = async (liberada) => {
    const rango = liberada.foja_termino - liberada.foja_inicio + 1;
    const toastId = toast.loading('Tomando foja liberada...');
    try {
      const { data } = await api.post('/fojas/reservar', {
        tipo_registro: liberada.tipo_registro,
        cantidad_fojas: rango
      });

      if (data && data.status === 'success') {
        toast.success('Foja liberada tomada y asignada a tu nombre.', { id: toastId });
        setMostrarLiberadas(false);
        setTipoRegistro(liberada.tipo_registro);
        fetchCorrelativosTodos();
        fetchPendientes();
        fetchFojasLiberadas();
      }
    } catch (error) {
      console.error('Error al tomar foja liberada:', error);
      toast.error(error.response?.data?.message || 'Error al tomar la foja liberada.', { id: toastId });
    }
  };

  return (
    <div className="container" style={{ paddingBottom: '4rem', maxWidth: '1280px' }}>
      {/* Encabezado Principal */}
      <header style={{ marginBottom: '2.5rem', marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Layers style={{ color: 'var(--copper)' }} /> Tomador de Fojas
          </h1>
          <p className="section-subtitle">
            Reserva concurrente de correlativos por libro con sincronización en tiempo real.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ position: 'relative' }}>
            <button
              className="btn"
              onClick={() => setMostrarLiberadas(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: fojasLiberadas.length > 0 ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                border: `1px solid ${fojasLiberadas.length > 0 ? 'rgba(245, 158, 11, 0.4)' : 'var(--border-color)'}`,
                color: fojasLiberadas.length > 0 ? '#b45309' : 'var(--text-muted)',
                fontWeight: 700
              }}
            >
              <Bell size={16} /> Fojas Liberadas: {fojasLiberadas.length}
            </button>

            {mostrarLiberadas && (
              <div className="glass-card animate-scale-in" style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: '380px', zIndex: 50,
                padding: '1rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)', border: '1px solid var(--border-color)',
                background: 'var(--bg-surface)', maxHeight: '360px', overflowY: 'auto'
              }}>
                {fojasLiberadas.length === 0 ? (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                    No hay fojas liberadas pendientes de reasignar.
                  </p>
                ) : fojasLiberadas.map((lib) => (
                  <div key={lib.id} style={{ borderBottom: '1px solid var(--border-color)', padding: '10px 0' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--primary-dark)' }}>
                      {BOOKS.find(b => b.id === lib.tipo_registro)?.name || lib.tipo_registro}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '2px 0' }}>
                      Foja {lib.foja_inicio}{lib.foja_termino !== lib.foja_inicio ? `–${lib.foja_termino}` : ''} · Número {lib.numero} · {lib.anio}
                    </div>
                    {lib.descripcion && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic', margin: '2px 0' }}>
                        "{lib.descripcion}"
                      </div>
                    )}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleTomarLiberada(lib)}
                      style={{ marginTop: '6px', padding: '3px 10px', fontSize: '0.72rem' }}
                    >
                      Tomar esta foja
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-secondary" onClick={() => { fetchUltimoCorrelativo(tipoRegistro); fetchPendientes(); fetchFojasLiberadas(); }} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RefreshCw size={16} /> Sincronizar
          </button>
        </div>
      </header>

      {/* Grid de Libros / Libros Electrónicos */}
      <section style={{ marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BookOpen size={18} /> Selección de Libros (Registros)
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '1rem' }}>
          {BOOKS.map((book) => {
            const cache = correlativosLibros[book.id];
            const isSelected = tipoRegistro === book.id;
            const liberadasDelLibro = fojasLiberadas.filter(l => l.tipo_registro === book.id);
            return (
              <div
                key={book.id}
                onClick={() => setTipoRegistro(book.id)}
                style={{
                  background: 'var(--bg-surface)',
                  border: isSelected ? `2.5px solid ${book.color}` : '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '1.2rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: isSelected ? '0 8px 20px -6px rgba(0,0,0,0.08)' : 'none',
                  transform: isSelected ? 'translateY(-2px)' : 'none'
                }}
              >
                {/* Indicador de Selección */}
                {isSelected && (
                  <div style={{
                    position: 'absolute', top: 0, right: 0,
                    backgroundColor: book.color, color: 'white',
                    padding: '4px 10px', fontSize: '0.65rem', fontWeight: 700,
                    borderBottomLeftRadius: '8px'
                  }}>
                    ACTIVO
                  </div>
                )}

                {/* Alerta de foja liberada disponible HOY para este libro (trencito) */}
                {liberadasDelLibro.length > 0 && (
                  <div
                    title={`Hay ${liberadasDelLibro.length} foja${liberadasDelLibro.length > 1 ? 's' : ''} liberada${liberadasDelLibro.length > 1 ? 's' : ''} hoy para este libro, disponible${liberadasDelLibro.length > 1 ? 's' : ''} para tomar.`}
                    style={{
                      position: 'absolute', top: 0, left: 0,
                      backgroundColor: '#f59e0b', color: 'white',
                      padding: '4px 10px', fontSize: '0.65rem', fontWeight: 700,
                      borderBottomRightRadius: '8px',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    <Bell size={11} /> {liberadasDelLibro.length} LIBERADA{liberadasDelLibro.length > 1 ? 'S' : ''}
                  </div>
                )}

                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--primary-dark)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: book.color }}></span>
                  {book.name}
                </h3>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 12px 0', minHeight: '32px' }}>
                  {book.desc}
                </p>

                {/* Siguiente Correlativo Estimado en Badge */}
                <div style={{
                  background: book.bg,
                  border: `1px solid rgba(0,0,0,0.03)`,
                  borderRadius: '8px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>SIGUIENTE ESTIMADO:</span>
                  {cache ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: book.color, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      F: {cache.foja_inicio} | N: {cache.numero}
                      {cache.origen === 'liberada' && (
                        <span className="badge badge-warning" style={{ fontSize: '0.6rem', padding: '1px 4px', textTransform: 'uppercase' }} title="Esta foja viene de la cola de reciclaje (trencito) por liberación">
                          Trencito
                        </span>
                      )}
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Cargando...</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Panel de Trabajo: Formulario Izquierda y Tabla Derecha */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem', alignItems: 'start' }}>
        
        {/* Formulario de Toma de Fojas */}
        <section className="glass-card" style={{ padding: '2rem' }}>
          <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={18} /> Tomar Fojas
            </h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
              Bloquea correlativos de forma segura en la base de datos.
            </p>
          </div>

          <form onSubmit={handleReservar}>
            <div className="form-group" style={{ marginBottom: '1.2rem' }}>
              <label style={{ fontWeight: 600 }}>Libro Seleccionado</label>
              <input
                type="text"
                readOnly
                value={BOOKS.find(b => b.id === tipoRegistro)?.name || ''}
                style={{ background: 'var(--bg-body)', fontWeight: 700, color: 'var(--primary-dark)', border: '1px solid var(--border-color)' }}
              />
            </div>

            {siguienteCorrelativo && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.05)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '1.2rem',
                fontSize: '0.8rem'
              }}>
                <div style={{ fontWeight: 700, color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertCircle size={14} style={{ color: 'var(--copper)' }} />
                  Información del Correlativo
                </div>
                <div style={{ marginTop: '6px', color: 'var(--text-main)', lineHeight: '1.4' }}>
                  Se asignará el <strong>Número {siguienteCorrelativo.numero}</strong>, ocupando la
                  {cantidadFojas > 1 ? (
                    <> <strong>Foja {siguienteCorrelativo.foja_inicio}</strong> a la <strong>{siguienteCorrelativo.foja_inicio + cantidadFojas - 1}</strong></>
                  ) : (
                    <> <strong>Foja {siguienteCorrelativo.foja_inicio}</strong></>
                  )}.
                  {siguienteCorrelativo.origen === 'liberada' ? (
                    <p style={{ margin: '4px 0 0 0', color: 'var(--copper)', fontWeight: 600 }}>
                      * Proviene del pool de fojas liberadas (trencito) para rellenar saltos.
                    </p>
                  ) : (
                    <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                      * Correlativo continuo estándar.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: '2rem' }}>
              <label style={{ fontWeight: 600 }}>¿Cuántas fojas ocupa este número?</label>
              <input
                type="number"
                min="1"
                max="30"
                value={cantidadFojas}
                onChange={(e) => setCantidadFojas(Math.max(1, parseInt(e.target.value) || 1))}
                style={{ fontSize: '1rem', padding: '10px' }}
                placeholder="Ej: 1"
                required
              />
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                Un número es siempre único; si el documento es extenso puede abarcar varias fojas seguidas.
              </span>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: '12px',
                fontWeight: 700,
                fontSize: '0.92rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.15)'
              }}
            >
              Confirmar Toma de Foja
            </button>
          </form>
        </section>

        {/* Tabla de Fojas Pendientes */}
        <section className="glass-card" style={{ padding: '2rem' }}>
          <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.25rem', color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={18} /> Fojas Reservadas Pendientes
              </h2>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                Fojas tomadas que aún no han sido completadas con datos de intervinientes.
              </p>
            </div>
            <span style={{
              background: 'rgba(37,99,235,0.08)',
              color: 'var(--primary)',
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 700
            }}>
              {pendientes.length} Pendiente{pendientes.length !== 1 && 's'}
            </span>
          </div>

          {/* Barra de filtros: quién tomó la foja y en qué rango de fechas */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '1.2rem' }}>
            <div style={{ display: 'flex', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
              {[
                { id: 'mias', label: 'Mías' },
                { id: 'funcionario', label: 'Por funcionario' },
                { id: 'todas', label: 'Todas' }
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFiltroModo(opt.id)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    border: 'none',
                    cursor: 'pointer',
                    background: filtroModo === opt.id ? 'var(--primary)' : 'transparent',
                    color: filtroModo === opt.id ? 'white' : 'var(--text-muted)'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {filtroModo === 'funcionario' && (
              <select
                value={filtroFuncionarioId}
                onChange={(e) => setFiltroFuncionarioId(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '6px 8px' }}
              >
                <option value="">Seleccione funcionario...</option>
                {funcionarios.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
              <input
                type="date"
                value={fechaDesde}
                disabled={todasFechas}
                onChange={(e) => setFechaDesde(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '5px 6px' }}
              />
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>a</span>
              <input
                type="date"
                value={fechaHasta}
                disabled={todasFechas}
                onChange={(e) => setFechaHasta(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '5px 6px' }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={todasFechas} onChange={(e) => setTodasFechas(e.target.checked)} />
              Todas las fechas
            </label>
          </div>

          {loadingPendientes ? (
            <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
              <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 10px' }} />
              Cargando fojas pendientes...
            </div>
          ) : pendientes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1.5rem',
              background: 'rgba(0,0,0,0.02)',
              borderRadius: '8px',
              color: 'var(--text-muted)',
              fontSize: '0.85rem'
            }}>
              <CheckCircle size={28} style={{ color: 'var(--success)', marginBottom: '8px' }} />
              No tienes fojas pendientes de completación.
            </div>
          ) : (
            <div className="table-wrapper">
              <table style={{ width: '100%', fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th>Libro</th>
                    <th>Correlativo (F/N/A)</th>
                    <th>Tomado por</th>
                    <th>Trámite Relacionado</th>
                    <th>Fecha Reserva</th>
                    <th style={{ textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pendientes.map((item) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: 600 }}>
                        <span style={{
                          display: 'inline-block',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: BOOKS.find(b => b.id === item.tipo_registro)?.color || '#9ca3af',
                          marginRight: '6px'
                        }}></span>
                        {BOOKS.find(b => b.id === item.tipo_registro)?.name || item.tipo_registro}
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        {item.foja_inicio}{item.foja_termino !== item.foja_inicio ? `–${item.foja_termino}` : ''} / {item.numero} / {item.anio}
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>
                        {item.usuario_responsable?.name || <span style={{ fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td>
                        {item.solicitud ? (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
                              Solicitud #{item.solicitud.id}
                            </span>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                              {item.solicitud.tipo_tramite || 'Trámite'}
                            </span>
                          </div>
                        ) : (
                          <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>Ingreso Libre</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>
                        {new Date(item.created_at).toLocaleString('es-CL', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-sm"
                            onClick={() => navigate(`/ingreso-indice?registro_indice_id=${item.id}`)}
                            style={{
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            Completar <ExternalLink size={12} />
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => openLiberarModal(item.id)}
                            style={{
                              padding: '4px 8px',
                              fontSize: '0.75rem',
                              background: 'transparent',
                              border: '1px solid #fecaca',
                              color: '#ef4444',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Liberar foja (trencito)"
                          >
                            <Trash2 size={12} /> Liberar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Modal de Confirmación y Motivo de Liberación (Trencito) */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="glass-card animate-scale-in" style={{
            maxWidth: '480px',
            width: '100%',
            padding: '2rem',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-surface)'
          }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldAlert /> Liberar Foja al Trencito
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: '1.5', margin: '0 0 1.2rem 0' }}>
              ¿Está seguro de que desea liberar esta foja? Se eliminará de su lista de pendientes y pasará a estar disponible en el pool de reciclaje (trencito) para rellenar saltos correlativos en el próximo ingreso.
            </p>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Motivo de la liberación *</label>
              <textarea
                rows={3}
                placeholder="Ej: Trámite desistido por el usuario / error en cantidad de fojas tomadas."
                value={motivoLiberacion}
                onChange={(e) => setMotivoLiberacion(e.target.value)}
                required
                style={{ fontSize: '0.82rem', width: '100%', padding: '8px', borderRadius: '6px' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={submittingLiberar}
                onClick={() => setIsModalOpen(false)}
                style={{ padding: '6px 14px', fontSize: '0.8rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn"
                disabled={submittingLiberar || !motivoLiberacion.trim()}
                onClick={confirmLiberar}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '6px 14px',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {submittingLiberar ? 'Liberando...' : 'Confirmar Liberación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
